import { useRef, useState } from "preact/hooks";

import { HeatSpeciesList, heatTierDotClass } from "./HeatSpeciesList";
import { WeatherDetailOverlay } from "./WeatherDetailOverlay";
import { speciesTierLabel } from "../lib/weatherCopy";
import type { AdvisoryOut } from "../types";

export interface HeatAdvisorySectionProps {
  advisory: AdvisoryOut | null;
  /** True when at least one NWS alert is currently active
   * (`alerts.length > 0`) — C2's NWS-banner precedence gate (ratified
   * Addendum condition 4, BINDING): an active NWS alert suppresses the
   * hero to a plain pill regardless of the 2+-species escalation math.
   * Passed down rather than re-derived here so this component has no
   * dependency on the alerts shape itself, only the one boolean C2's
   * conditional needs. */
  alertsActive: boolean;
}

// Sprint-015 C5 — the local `tierDotClass`/`SpeciesList` helpers this file
// originally defined were extracted to the shared `HeatSpeciesList.tsx`
// (imported above as `heatTierDotClass`/`HeatSpeciesList`) so the Today
// Card pill's own detail sheet (`TodayHeatPill.tsx`) can reuse the SAME
// species-breakdown markup rather than a second copy-pasted
// implementation. This file's own rendered output/classes are unchanged
// by that move.

const TITLE_ID = "heat-advisory-title";

/**
 * Sprint-015 C1/C2 (weather-hub-ia-layout-2026-07-10.md §2/§3, CD sign-off
 * 2026-07-10). Renders nothing on `advisory === null` (farm-wide
 * all-clear — no green "all clear" chip, no placeholder, the element is
 * simply absent). Otherwise renders EITHER the one-line pill (C1, every
 * non-escalated state) OR the full hero (C2) — NEVER both.
 *
 * The NWS-precedence/escalation gate is implemented here as a real
 * conditional (the ratified Addendum condition 4, restated verbatim by
 * the C0 spec §3, BINDING — "the pill vs. hero branch must be gated on
 * `alerts.length` before it is gated on the species escalation math"),
 * never a CSS-only treatment:
 *
 *   IF an NWS alert is active           -> PILL only, regardless of the
 *                                           escalation math
 *   ELSE IF level==="high" AND
 *        high_species_count >= 2        -> HERO (`.heat-advisory`)
 *   ELSE                                -> PILL (C1's state table)
 */
export function HeatAdvisorySection({ advisory, alertsActive }: HeatAdvisorySectionProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const heroLinkRef = useRef<HTMLButtonElement>(null);

  if (!advisory) return null;

  const escalate = !alertsActive && advisory.level === "high" && advisory.high_species_count >= 2;
  const isModerate = advisory.level === "elevated";

  if (escalate) {
    return (
      <>
        <div class="heat-advisory">
          <span class="heat-sev-tag">Heat danger</span>
          <p class="heat-advisory-headline">{advisory.headline}</p>
          <ul class="heat-advisory-species">
            {advisory.species.map((s) => (
              <li key={s.species_label} class="heat-species-row">
                <span class={`heat-species-dot ${heatTierDotClass(s.tier)}`} aria-hidden="true" />
                <span class="heat-species-name">{s.species_label}</span>
                <span class="heat-species-tier">{speciesTierLabel(s.tier)}</span>
              </li>
            ))}
          </ul>
          <button
            ref={heroLinkRef}
            type="button"
            class="heat-advisory-link"
            onClick={() => setDetailOpen(true)}
          >
            See the full breakdown &amp; times →
          </button>
        </div>
        <WeatherDetailOverlay
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          titleId={TITLE_ID}
          title="Heat-stress advisory"
          triggerRef={heroLinkRef}
        >
          <p class="heat-sheet-sub">{advisory.headline}</p>
          <HeatSpeciesList species={advisory.species} />
        </WeatherDetailOverlay>
      </>
    );
  }

  return (
    <>
      <div class="heat-pill-anchor">
        <button
          ref={pillRef}
          type="button"
          class={`heat-pill${isModerate ? " is-moderate" : ""}`}
          aria-haspopup="dialog"
          onClick={() => setDetailOpen(true)}
        >
          <span class="heat-pill-icon" aria-hidden="true">
            🌡️
          </span>
          {advisory.headline}
          <span class="heat-pill-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      </div>
      <WeatherDetailOverlay
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        titleId={TITLE_ID}
        title="Heat-stress advisory"
        triggerRef={pillRef}
      >
        <p class="heat-sheet-sub">{advisory.headline}</p>
        <HeatSpeciesList species={advisory.species} />
      </WeatherDetailOverlay>
    </>
  );
}
